import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { collectionService } from '../../services/collectionService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { DocumentArrowDownIcon, PrinterIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const CollectionReport = () => {
  const [filters, setFilters] = useState({
    from_date: new Date(new Date().setDate(1)).toISOString().split('T')[0],
    to_date: new Date().toISOString().split('T')[0],
    samity_id: '',
    officer_id: ''
  });

  const { data, isLoading, refetch } = useQuery(
    ['collection-report', filters],
    () => collectionService.getCollectionReport(filters),
    { enabled: true }
  );

  const handleExport = async () => {
    try {
      const blob = await collectionService.exportCollectionReport(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `collection_report_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Report exported successfully');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const reportData = data?.data;
  const collections = reportData?.collections || [];
  const summary = reportData?.summary || {};
  const dailySummary = reportData?.daily_summary || [];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Collection Report</h1>
          <p className="text-gray-600">Daily collection summary and analysis</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            Export Excel
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center"
          >
            <PrinterIcon className="h-5 w-5 mr-2" />
            Print
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">From Date</label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">To Date</label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Samity</label>
            <select
              value={filters.samity_id}
              onChange={(e) => setFilters({ ...filters, samity_id: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Samities</option>
              {/* Add samity options from API */}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Field Officer</label>
            <select
              value={filters.officer_id}
              onChange={(e) => setFilters({ ...filters, officer_id: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">All Officers</option>
              {/* Add officer options from API */}
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Generate Report
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="text-sm text-blue-600">Total Collection</div>
          <div className="text-2xl font-bold text-blue-700">
            {formatCurrency(summary.total_collection || 0)}
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-sm text-green-600">Total Savings</div>
          <div className="text-2xl font-bold text-green-700">
            {formatCurrency(summary.total_savings || 0)}
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="text-sm text-purple-600">Loan Collection</div>
          <div className="text-2xl font-bold text-purple-700">
            {formatCurrency(summary.total_loan_collection || 0)}
          </div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="text-sm text-orange-600">Average Per Day</div>
          <div className="text-2xl font-bold text-orange-700">
            {formatCurrency(summary.average_per_day || 0)}
          </div>
        </div>
      </div>

      {/* Daily Summary Chart */}
      {dailySummary.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">Daily Collection Trend</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Date</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Savings</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Loan</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Total</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Sheets</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Samities</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {dailySummary.map((day, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm">{formatDate(day.collection_date)}</td>
                    <td className="px-4 py-2 text-sm text-right text-green-600">
                      {formatCurrency(day.daily_savings)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-blue-600">
                      {formatCurrency(day.daily_loan)}
                    </td>
                    <td className="px-4 py-2 text-sm text-right font-bold">
                      {formatCurrency(day.daily_total)}
                    </td>
                    <td className="px-4 py-2 text-sm text-center">{day.sheets_count}</td>
                    <td className="px-4 py-2 text-sm text-center">{day.samities_covered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detailed Collections Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Collection Details</h3>
        </div>
        {collections.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No collection data found for the selected period
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Samity</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Officer</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Savings</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Loan</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Members</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Items</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {collections.map((collection) => (
                  <tr key={collection.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(collection.collection_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {collection.samity_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {collection.officer_name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600">
                      {formatCurrency(collection.total_savings)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-blue-600">
                      {formatCurrency(collection.total_loan_installments)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold">
                      {formatCurrency(collection.total_collection)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      {collection.members_served}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      {collection.total_items}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="3" className="px-6 py-4 text-right font-bold">Total:</td>
                  <td className="px-6 py-4 text-right font-bold text-green-600">
                    {formatCurrency(summary.total_savings || 0)}
                  </td>
                  <td className="px-6 py-4 text-right font-bold text-blue-600">
                    {formatCurrency(summary.total_loan_collection || 0)}
                  </td>
                  <td className="px-6 py-4 text-right font-bold">
                    {formatCurrency(summary.total_collection || 0)}
                  </td>
                  <td className="px-6 py-4 text-center font-bold">
                    {summary.total_members_served || 0}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionReport;